import React, { useState } from 'react';
import { ChevronRightIcon, CheckCircleIcon, ArrowUpDownIcon } from 'lucide-react';
import { Invoice } from '../../lib/thetape-data';
interface InvoiceListProps {
  invoices: Invoice[];
  onSelectInvoice: (invoice: Invoice) => void;
}
type SortField = 'id' | 'amount' | 'obligor' | 'dueDate' | 'tenorDays';
type SortDirection = 'asc' | 'desc';
export function InvoiceList({
  invoices,
  onSelectInvoice
}: InvoiceListProps) {
  const [sortField, setSortField] = useState<SortField>('amount');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };
  const sortedInvoices = [...invoices].sort((a, b) => {
    const modifier = sortDirection === 'asc' ? 1 : -1;
    if (sortField === 'amount') return (a.amount - b.amount) * modifier;
    if (sortField === 'tenorDays') return (a.tenorDays - b.tenorDays) * modifier;
    if (sortField === 'id') return a.id.localeCompare(b.id) * modifier;
    if (sortField === 'obligor') return a.obligor.localeCompare(b.obligor) * modifier;
    if (sortField === 'dueDate') return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime() * modifier;
    return 0;
  });
  const formatCurrency = (val: number) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(val);
  return <div className="w-full overflow-hidden border border-slate-800 bg-slate-900" data-id="element-345">
      <div className="overflow-x-auto" data-id="element-346">
        <table className="w-full text-left text-sm" data-id="element-347">
          <thead data-id="element-348">
            <tr className="border-b border-slate-800 bg-slate-950/50 text-xs font-mono text-slate-400 uppercase tracking-wider" data-id="element-349">
              <th className="px-4 py-3 font-medium cursor-pointer hover:text-slate-200" onClick={() => handleSort('id')} data-id="element-350">
                <div className="flex items-center gap-1" data-id="element-351">
                  Invoice ID <ArrowUpDownIcon size={12} data-id="element-352" />
                </div>
              </th>
              <th className="px-4 py-3 font-medium cursor-pointer hover:text-slate-200" onClick={() => handleSort('obligor')} data-id="element-353">
                <div className="flex items-center gap-1" data-id="element-354">
                  Obligor <ArrowUpDownIcon size={12} data-id="element-355" />
                </div>
              </th>
              <th className="px-4 py-3 font-medium text-right cursor-pointer hover:text-slate-200" onClick={() => handleSort('amount')} data-id="element-356">
                <div className="flex items-center justify-end gap-1" data-id="element-357">
                  Amount <ArrowUpDownIcon size={12} data-id="element-358" />
                </div>
              </th>
              <th className="px-4 py-3 font-medium text-center" data-id="element-359">Rating</th>
              <th className="px-4 py-3 font-medium cursor-pointer hover:text-slate-200" onClick={() => handleSort('dueDate')} data-id="element-360">
                <div className="flex items-center gap-1" data-id="element-361">
                  Due Date <ArrowUpDownIcon size={12} data-id="element-362" />
                </div>
              </th>
              <th className="px-4 py-3 font-medium text-center cursor-pointer hover:text-slate-200" onClick={() => handleSort('tenorDays')} data-id="element-363">
                <div className="flex items-center justify-center gap-1" data-id="element-364">
                  Tenor <ArrowUpDownIcon size={12} data-id="element-365" />
                </div>
              </th>
              <th className="px-4 py-3 font-medium text-center" data-id="element-366">Status</th>
              <th className="px-4 py-3 font-medium" data-id="element-367"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800" data-id="element-368">
            {sortedInvoices.map(invoice => <tr key={invoice.id} onClick={() => onSelectInvoice(invoice)} className="group cursor-pointer hover:bg-slate-800/50 transition-colors" data-id="element-369">
                <td className="px-4 py-3 font-mono text-slate-300 group-hover:text-white" data-id="element-370">
                  {invoice.id}
                </td>
                <td className="px-4 py-3 text-slate-300 font-medium" data-id="element-371">
                  {invoice.obligor}
                </td>
                <td className="px-4 py-3 font-mono text-right text-slate-300" data-id="element-372">
                  {formatCurrency(invoice.amount)}
                </td>
                <td className="px-4 py-3 text-center" data-id="element-373">
                  <span className={`inline-block px-2 py-0.5 text-xs font-mono rounded border ${invoice.creditRating.startsWith('A') ? 'bg-emerald-950/30 border-emerald-900 text-emerald-400' : 'bg-amber-950/30 border-amber-900 text-amber-400'}`} data-id="element-374">
                    {invoice.creditRating}
                  </span>
                </td>
                <td className="px-4 py-3 font-mono text-slate-400 text-xs" data-id="element-375">
                  {invoice.dueDate}
                </td>
                <td className="px-4 py-3 font-mono text-center text-slate-400" data-id="element-376">
                  {invoice.tenorDays}d
                </td>
                <td className="px-4 py-3 text-center" data-id="element-377">
                  <div className="flex items-center justify-center gap-1.5" data-id="element-378">
                    <CheckCircleIcon size={14} className="text-emerald-500" data-id="element-379" />
                    <span className="text-xs font-mono text-emerald-500" data-id="element-380">
                      13/13
                    </span>
                  </div>
                </td>
                <td className="px-4 py-3 text-right text-slate-600 group-hover:text-slate-400" data-id="element-381">
                  <ChevronRightIcon size={16} data-id="element-382" />
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
}